class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // The section response replaces #CartDrawer, not the custom element that
    // owns it. When the first items are added, the server returns a populated
    // drawer but the root can therefore retain its initial `is-empty` class;
    // component-cart-drawer.css then hides the populated contents. Clear both
    // empty-state markers before inserting the response.
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/*
 * Collection mates: the multi-add that used to be the "Complete the
 * collection" PDP section (retired 2026-08-07). Every cart mutation replaces
 * this element's DOM through the Section Rendering API, so the handler has to
 * be a custom element — connectedCallback re-binds on each re-render, and an
 * inline <script> re-inserted via innerHTML would never execute.
 */
class CartCollectionMates extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-cc-mates-add]');
    this.errorEl = this.querySelector('[data-cc-mates-error]');
    if (!this.button) return;

    this.cartEligibleCount = parseInt(this.dataset.cartEligibleCount, 10) || 0;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-cc-mates-item]')) this.sync();
    });
    this.button.addEventListener('click', () => this.addAll());
    this.sync();
  }

  checked() {
    return Array.from(this.querySelectorAll('[data-cc-mates-item]')).filter((el) => el.checked);
  }

  formatMoney(cents) {
    return this.moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, (cents / 100).toFixed(2));
  }

  sync() {
    const picked = this.checked();
    this.button.disabled = picked.length === 0;

    /* Shopify applies the bundle discount per line item and rounds each one
       down, so summing first and taking a percentage lands a cent off what
       the cart actually charges. Mirror its per-line floor. Only the newly
       selected lines are counted — the lines already in the cart show their
       own allocations in the re-rendered drawer, so claiming them here would
       double-count. */
    const eligible = picked.filter((el) => el.dataset.discountable === 'true');
    let saving = 0;
    if (this.cartEligibleCount + eligible.length >= 2) {
      saving = eligible.reduce(
        (sum, el) => sum + Math.floor((parseInt(el.dataset.priceCents, 10) * 10) / 100),
        0
      );
    }

    const total = picked.reduce((sum, el) => sum + parseInt(el.dataset.priceCents, 10), 0);
    const label = this.button.dataset.labelBase || this.button.textContent.trim();
    this.button.dataset.labelBase = label;
    this.button.textContent =
      picked.length === 0
        ? label
        : saving > 0
          ? `${label} · ${this.formatMoney(total - saving)} (save ${this.formatMoney(saving)})`
          : `${label} · ${this.formatMoney(total)}`;
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  addAll() {
    const picked = this.checked();
    if (!picked.length) return;
    if (this.errorEl) this.errorEl.hidden = true;

    this.button.classList.add('loading');
    this.button.disabled = true;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const config = fetchConfig('javascript');
    config.body = JSON.stringify({
      items: picked.map((el) => ({ id: Number(el.dataset.variantId), quantity: 1 })),
      sections: cart ? cart.getSectionsToRender().map((s) => s.id) : [],
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          /* /cart/add.js is all-or-nothing: one blocked line stops the whole
             batch, so say which one. */
          const desc = response.description || response.message || 'Could not add these mats.';
          this.showError(`${desc} Nothing was added — uncheck the unavailable mat and try again.`);
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-collection-mates',
            cartData: response,
          });
        }
        if (cart) {
          if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
          cart.renderContents(response);
        } else {
          window.location = routes.cart_url;
        }
      })
      .catch(() => {
        this.showError('Something went wrong adding these. Please try again.');
      })
      .finally(() => {
        this.button.classList.remove('loading');
        this.button.disabled = false;
      });
  }
}

customElements.define('cart-collection-mates', CartCollectionMates);

// The "Don't Stop Here" carousel lives inside `.drawer__inner`, which
// CartDrawer.renderContents() and CartItems.updateQuantity() both replace
// wholesale via the Section Rendering API. A single top-level `new Swiper()` at
// defer time therefore bound to an element that gets thrown away on the first
// add-to-cart, leaving a dead slider — no sizing, no transform, no pagination —
// for the rest of the session, which is precisely when a shopper would see it.
//
// The track is also only rendered when `cart.item_count > 0` now (see
// snippets/cart-drawer.liquid), so on a fresh page view there is nothing to bind
// to at all. Both facts mean init has to be repeatable and defensive.
var cartShopSwiper = null;

function initCartShopSwiper() {
  // Guarded so this file is safe whether swiper-bundle loads before or after it.
  if (typeof Swiper === 'undefined') return;

  var el = document.querySelector('.cartShop');
  if (!el) return;
  // Swiper marks its own root; re-initialising over a live instance leaks it.
  if (el.swiper) return;

  if (cartShopSwiper && cartShopSwiper.destroyed !== true) {
    try {
      cartShopSwiper.destroy(true, true);
    } catch (e) {
      /* the element it owned is already detached */
    }
  }

  cartShopSwiper = new Swiper(el, {
    slidesPerView: 1.2,
    spaceBetween: 16,
    breakpoints: {
      767: {
        slidesPerView: 1.2,
        spaceBetween: 20,
      },
      1024: {
        slidesPerView: 1.2,
        spaceBetween: 24,
      },
    },
  });
}

// The Libautech "Add Upsell & Cross-sell" app embed appends its cart-drawer
// widget (`<div id="lbtu-embed-placeholder-<offerId>"
// class="lbtu-embed-cartDrawerAddons">`) to the END of `<cart-drawer-items>` —
// its default target list is cart-drawer-items / .cart-drawer__items /
// .cart-drawer__line-items with position "end". In this theme the last child of
// `<cart-drawer-items>` is the "Keep exploring" rail (`.cart_shop_collec`, a
// 10-card swiper), so the widget lands ~1100px down inside a ~640px scroll area
// and no shopper ever sees it. Moving it to sit directly after the line-item
// form (`#CartDrawer-Form`) puts it where an upsell belongs: right under the
// items, above the rail.
//
// Done theme-side rather than via the app's "custom cart drawer selector"
// (#CartDrawer-Form, position "after") because the theme move is deterministic:
// it does not depend on the app setting surviving a reinstall or on the app
// resolving its selector before or after our Section Rendering re-renders.
//
// Constraints that shape the implementation:
// - The app sometimes injects several nodes with the SAME id (empty h=0
//   duplicates appear on cart changes) and React may later render into any of
//   them, so every `.lbtu-embed-cartDrawerAddons` is moved and NONE is removed.
//   Moving a node keeps its React root alive, and the app's own existence check
//   (document.querySelector('#lbtu-embed-placeholder-<id>')) still passes.
// - This runs from the same MutationObserver that re-inits the rail swiper, and
//   `after()` itself is a childList mutation. The move is therefore guarded
//   (only nodes not already in place get touched) so the second pass is a no-op
//   and the observer cannot feed itself.
function placeCartDrawerUpsells() {
  var items = document.querySelector('cart-drawer-items');
  if (!items) return;

  var form = items.querySelector('#CartDrawer-Form');
  if (!form) return;

  var anchor = form;
  // Snapshot the children first: `after()` reorders the live collection.
  Array.prototype.slice.call(items.children).forEach(function (node) {
    if (!node.classList || !node.classList.contains('lbtu-embed-cartDrawerAddons')) return;
    // Move only when needed — an unconditional `after()` would re-fire the
    // observer on every pass even when nothing changed.
    if (node.previousElementSibling !== anchor) anchor.after(node);
    anchor = node;
  });
}

// Observing the drawer beats hooking each re-render path: renderContents,
// updateQuantity and any future caller all mutate this subtree, and the observer
// cannot fall out of sync with them the way an enumerated list would. The same
// observer also catches the upsell app injecting its widget, which happens on
// its own timer after our re-renders.
function watchCartDrawerForSwiper() {
  var drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  initCartShopSwiper();
  placeCartDrawerUpsells();

  new MutationObserver(function () {
    initCartShopSwiper();
    placeCartDrawerUpsells();
  }).observe(drawer, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchCartDrawerForSwiper);
} else {
  watchCartDrawerForSwiper();
}
